import { useEffect, useRef, type ReactNode } from 'react';
import { SignedIn, SignedOut, RedirectToSignIn, useAuth, clerkOriginBlocked } from '../lib/auth';
import { AuthLoadingScreen } from './AuthLoadingScreen';
import { ClerkOriginBlockedScreen } from './ClerkOriginBlockedScreen';
import { capture } from '../lib/analytics';
import { consumePendingCallback, sessionStorageOrNull } from '../lib/authCallbackPending';
import { elapsedMsSince, monotonicNow } from '../lib/elapsedMs';

/**
 * Guards a route behind Clerk auth. Pre-hydration (isLoaded false), Clerk's
 * <SignedIn> and <SignedOut> BOTH render null, which left protected routes
 * blank while clerk-js loaded and permanently blank when the CDN-hotloaded
 * script never arrived. Show an explicit loading/failure screen until Clerk
 * resolves, then fall through to the unchanged SignedIn/SignedOut pair.
 *
 * Lives in its own file (not App.tsx) so it can run under jest: App.tsx reads
 * import.meta.env inline, which ts-jest CJS cannot compile (the same reason
 * lib/devBuild.ts was extracted from Layout).
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  // A production Clerk key is locked to app.overlayqa.com and its subdomains.
  // When the bundle is served elsewhere -- the team-review proxy renders it on
  // the Railway proxy host -- clerk-js can never complete its handshake, so the
  // loading screen below would wait, report a fake auth_load_failed, and offer
  // a Retry that can never succeed. Show honest copy instead. clerkOriginBlocked
  // is a module const, so this outer component calls no hooks and takes the same
  // branch for its whole life (rules-of-hooks holds).
  if (clerkOriginBlocked) return <ClerkOriginBlockedScreen />;
  return <ClerkGuardedRoute>{children}</ClerkGuardedRoute>;
}

function ClerkGuardedRoute({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();

  // A completed OAuth callback is reported HERE, on the page the user lands on.
  // The callback page cannot: clerk-js 5.128.0 setActive clears the session into
  // a transitive state, awaits a full-page navigate, and returns while the page
  // is unloading before the hydrated session is emitted, so its poll and
  // listener never see success (PostHog 2026-09-05..10-05: 48 entered, 46
  // landed signed in, 2 completed events). AuthCallbackPage leaves a
  // sessionStorage note at entry; consuming it (read + remove, with a TTL) once
  // Clerk hydrates signed-in is the one observation that cannot be cut short.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    const landed = consumePendingCallback(sessionStorageOrNull(), Date.now());
    if (!landed) return;
    capture('auth_callback_completed', {
      method: 'oauth',
      flow: 'builtin_component',
      delivered_from: 'landing',
      ...landed,
    });
  }, [isLoaded, isSignedIn]);

  // How long clerk-js actually took, reported once per mount that had to wait.
  //
  // This lives here rather than in AuthLoadingScreen because this is the only
  // component that observes the real false -> true transition; AuthLoadingScreen
  // just gets unmounted, and an unmount cannot distinguish "Clerk loaded" from
  // "user navigated away" or from StrictMode's synthetic remount.
  //
  // Two honest limits on the resulting sample, both by construction: it only
  // counts loads that were slow enough to render the loading screen at all, so
  // instant hydration is absent (the slow tail is what we want, but this is not
  // a whole-population figure); and it is per mount, so a session that visits
  // several protected routes reports only its first wait.
  const waitStartedAtRef = useRef(monotonicNow());
  const sawLoadingRef = useRef(false);
  const successReportedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded) {
      sawLoadingRef.current = true;
      return;
    }
    // Never waited (Clerk was already hydrated) -- not a sample.
    if (!sawLoadingRef.current || successReportedRef.current) return;
    successReportedRef.current = true;
    capture('auth_load_succeeded', {
      surface: 'protected_route',
      waited_ms: elapsedMsSince(waitStartedAtRef.current),
    });
  }, [isLoaded]);

  if (!isLoaded) return <AuthLoadingScreen />;

  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}
